import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
  Box,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import { OperationLogPreview } from "../services/operationLogService";
import { TestTarget } from "../types/TestTarget";

export interface PathSelection {
  path: string;
  domain?: string | null;
  viewId: string;
  serviceId?: string | null;
}

interface GroupPreviewDialogProps {
  open: boolean;
  preview: OperationLogPreview | null;
  services: TestTarget[];
  views: TestTarget[];
  onClose: () => void;
  onConfirm: (selections: PathSelection[]) => void;
  processing?: boolean;
}

export const GroupPreviewDialog: React.FC<GroupPreviewDialogProps> = ({
  open,
  preview,
  services,
  views,
  onClose,
  onConfirm,
  processing = false,
}) => {
  const [selectionMap, setSelectionMap] = useState<Record<number, PathSelection>>({});
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [editTargetIndex, setEditTargetIndex] = useState<number | null>(null);
  const [editServiceId, setEditServiceId] = useState<string>("");
  const [editViewId, setEditViewId] = useState<string>("");

  const viewsByService = useMemo(() => {
    const map = new Map<string, TestTarget[]>();
    views.forEach((view) => {
      const key = view.parentId || view.parent_test_target_id || "";
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)?.push(view);
    });
    return map;
  }, [views]);

  useEffect(() => {
    if (!preview) {
      setSelectionMap({});
      return;
    }
    const next: Record<number, PathSelection> = {};
    preview.path_matches.forEach((m, index) => {
      const hasCandidates = (m.candidates || []).length > 0;
      const hasConflict = (m.candidates || []).length > 1;
      const hasNoMatch = (m.candidates || []).length === 0;
      // 候補が1つの場合のみ自動設定（備考が表示される場合は初期状態では設定しない）
      if (m.matched_view_id && hasCandidates && !hasConflict && !hasNoMatch && !m.note) {
        next[index] = {
          path: m.path,
          domain: m.domain,
          viewId: m.matched_view_id,
          serviceId: m.matched_service_id,
        };
      }
    });
    setSelectionMap(next);
    setSelectionError(null);
  }, [preview]);

  if (!preview) {
    return null;
  }

  const openEditDialog = (index: number) => {
    const current = selectionMap[index];
    const matched = preview.path_matches[index];
    const serviceId =
      current?.serviceId ||
      matched?.matched_service_id ||
      matched?.candidates[0]?.service_id ||
      services[0]?.id ||
      services[0]?.test_target_id ||
      "";
    const viewId =
      current?.viewId ||
      matched?.matched_view_id ||
      matched?.candidates.find((c) => c.service_id === serviceId)?.view_id ||
      matched?.candidates[0]?.view_id ||
      "";
    setEditTargetIndex(index);
    setEditServiceId(serviceId || "");
    setEditViewId(viewId || "");
  };

  const closeEditDialog = () => {
    setEditTargetIndex(null);
    setEditServiceId("");
    setEditViewId("");
  };

  const handleApplyEdit = () => {
    if (editTargetIndex === null || !editViewId) {
      setSelectionError("Viewを選択してください");
      return;
    }
    const matchedPath = preview.path_matches[editTargetIndex]?.path || "";
    const matchedDomain = preview.path_matches[editTargetIndex]?.domain || null;
    // NOTE: 「新規Viewとして処理」を選択した場合、selectionMapから削除して「なし」表示にする
    if (editViewId === "__NEW_VIEW__") {
      setSelectionMap((prev) => {
        const next = { ...prev };
        delete next[editTargetIndex];
        return next;
      });
    } else {
      setSelectionMap((prev) => ({
        ...prev,
        [editTargetIndex]: {
          path: matchedPath,
          domain: matchedDomain,
          viewId: editViewId,
          serviceId: editServiceId,
        },
      }));
    }
    setSelectionError(null);
    closeEditDialog();
  };

  const handleConfirm = () => {
    // 明示的に選択されたもののみ反映し、未選択は従来の新規作成フローに委ねる
    onConfirm(Object.values(selectionMap));
  };

  const currentEditCandidates =
    editServiceId && viewsByService.get(editServiceId)
      ? viewsByService.get(editServiceId) || []
      : [];

  return (
    <>
      <Dialog open={open} onClose={processing ? undefined : onClose} maxWidth="lg" fullWidth>
        <DialogTitle>操作ログのインポート処理のプレビュー</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {selectionError && (
            <Alert severity="warning" onClose={() => setSelectionError(null)}>
              {selectionError}
            </Alert>
          )}

          <Box>
            <Typography variant="h6" gutterBottom>
              操作ログ内のpathとインポート対象のView
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Viewが「なし」「選択が必要です」になっているものは新規Viewを作成して登録します。
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#e5e7eb' }}>
                    <TableCell>操作ログのドメイン</TableCell>
                    <TableCell>操作ログのpath</TableCell>
                    <TableCell>pathにマッチするViewターゲット</TableCell>
                    <TableCell>Viewが所属するService</TableCell>
                    <TableCell>備考</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.path_matches.map((match, index) => {
                    const selection = selectionMap[index];
                    const hasConflict = (match.candidates || []).length > 1;
                    const hasNoMatch = (match.candidates || []).length === 0;
                    const hasNote = Boolean(match.note);
                    // 備考が表示される場合（候補が複数、または候補がない）で、選択がない場合は「なし」を表示
                    const shouldShowNone = (hasConflict || hasNoMatch || hasNote) && !selection?.viewId;
                    
                    const selectedView = shouldShowNone
                      ? null
                      : selection?.viewId
                        ? views.find(
                            (v) => v.id === selection.viewId || v.test_target_id === selection.viewId
                          ) || null
                        : null;
                    const selectedService = shouldShowNone
                      ? null
                      : selectedView
                        ? services.find(
                            (s) =>
                              s.id === selection?.serviceId ||
                              s.test_target_id === selection?.serviceId
                          ) ||
                          services.find(
                            (s) =>
                              s.id === selectedView?.parentId ||
                              s.test_target_id === selectedView?.parentId ||
                              s.id === selectedView?.parent_test_target_id ||
                              s.test_target_id === selectedView?.parent_test_target_id
                          ) ||
                          null
                        : null;
                    const needsSelection = hasConflict && !selection?.viewId;
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                            {match.domain || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>{match.path}</TableCell>
                        <TableCell>
                          {needsSelection ? (
                            <Typography color="error" variant="body2">
                              選択が必要です
                            </Typography>
                          ) : selectedView ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">{selectedView.name}</Typography>
                            </Stack>
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              なし
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {needsSelection ? (
                            <Typography color="error" variant="body2">
                              選択が必要です
                            </Typography>
                          ) : selectedService ? (
                            selectedService.name
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              なし
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {match.note && (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <WarningAmberIcon
                                  fontSize="small"
                                  color="warning"
                                  aria-label="warning"
                                />
                                <Typography variant="body2">{match.note}</Typography>
                              </Stack>
                            )}
                            {!match.note && match.candidates.length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                マッチするViewがありませんでした。Viewを新規に作成します。
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => openEditDialog(index)}
                            disabled={processing}
                          >
                            編集
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              操作ログ内のpathにマッチしないView
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#e5e7eb' }}>
                    <TableCell>View名</TableCell>
                    <TableCell>パス</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>備考</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.unmatched_views.map((view) => (
                    <TableRow key={view.view_id}>
                      <TableCell>{view.view_name}</TableCell>
                      <TableCell>
                        {view.path || (
                          <Typography color="text.secondary" variant="body2">
                            pathが設定されていません
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {view.service_name || (
                          <Typography color="text.secondary" variant="body2">
                            未設定
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {view.note || ""}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {preview.unmatched_views.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                        操作ログ内のpathにマッチしないViewがありません。
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={processing}>
            {processing ? (
              <>
                <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                処理中...
              </>
            ) : (
              "実行"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editTargetIndex !== null} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Viewを選択</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DialogContentText>ServiceとViewを選択してください。</DialogContentText>
          <FormControl fullWidth size="small">
            <InputLabel id="preview-service-label">Service</InputLabel>
            <Select
              labelId="preview-service-label"
              label="Service"
              value={editServiceId}
              onChange={(e) => {
                const nextServiceId = e.target.value;
                setEditServiceId(nextServiceId);
                const serviceViews = viewsByService.get(nextServiceId) || [];
                setEditViewId(serviceViews[0]?.id || serviceViews[0]?.test_target_id || "");
              }}
            >
              {services.map((service) => (
                <MenuItem key={service.id || service.test_target_id} value={service.id || service.test_target_id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="preview-view-label">View</InputLabel>
            <Select
              labelId="preview-view-label"
              label="View"
              value={editViewId}
              onChange={(e) => setEditViewId(e.target.value)}
            >
              <MenuItem value="__NEW_VIEW__">
                <Typography color="primary">新規Viewとして処理</Typography>
              </MenuItem>
              {currentEditCandidates.map((view) => (
                <MenuItem key={view.id || view.test_target_id} value={view.id || view.test_target_id}>
                  {view.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>キャンセル</Button>
          <Button onClick={handleApplyEdit} variant="contained" disabled={!editViewId}>
            適用
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
